import { addDays, isBefore } from "date-fns";

import { RouteEntities } from "../../../core/routes/relativeRoutes";
import { ExclusiveLensStatus, LensCreatorPayoutEligibility, Visibility } from "../../../gql-generated/types";
import { buildInsightsTabPath } from "../../../lens-insights/utils/insightsNavigation";
import { MonetizationTab } from "../../../monetization/models";
import { buildMonetizationTabPath } from "../../../monetization/utils";
import { CREATOR_REWARDS_DAYS_THRESHOLD } from "../constants";

interface SectionVisibility {
    shouldShowExclusiveLensToggle: boolean;
    shouldShowRemovedExclusiveLensWarning: boolean;
    shouldShowTopPerformerPayoutsSection: boolean;
    shouldShowInsights: boolean;
}

interface ViewFullHistoryVisibility {
    creatorPayoutEligible: boolean;
    isExclusiveLens: boolean;
    isMonetizationInsightsEnabled: boolean;
    isRemovedFromLensPlus: boolean;
}

export const shouldShowViewFullHistory = ({
    creatorPayoutEligible,
    isExclusiveLens,
    isMonetizationInsightsEnabled,
    isRemovedFromLensPlus,
}: ViewFullHistoryVisibility): boolean =>
    creatorPayoutEligible || (!isRemovedFromLensPlus && (isMonetizationInsightsEnabled || isExclusiveLens));

export const calculateSectionVisibility = (
    isMyLensesCreatorPayoutEnabled: boolean,
    showLensCreatorRewardsDetails: boolean,
    lensPlusEligible: boolean,
    exclusiveLensStatus: ExclusiveLensStatus,
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility,
): SectionVisibility => {
    const lensPlusEligibleAndShowExclusiveLensToggle =
        lensPlusEligible &&
        (exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusEligible ||
            exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive);
    const isRemovedFromLensPlus = exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusRemoved;

    const shouldShowExclusiveLensToggle =
        isMyLensesCreatorPayoutEnabled &&
        !isRemovedFromLensPlus &&
        (!lensPlusEligible || lensPlusEligibleAndShowExclusiveLensToggle);
    const shouldShowRemovedExclusiveLensWarning = isMyLensesCreatorPayoutEnabled && isRemovedFromLensPlus;

    const shouldShowTopPerformerPayoutsSection =
        isMyLensesCreatorPayoutEnabled &&
        (lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible ||
            !lensPlusEligibleAndShowExclusiveLensToggle);

    const shouldShowInsights =
        showLensCreatorRewardsDetails &&
        lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible;

    return {
        shouldShowExclusiveLensToggle,
        shouldShowRemovedExclusiveLensWarning,
        shouldShowTopPerformerPayoutsSection,
        shouldShowInsights,
    };
};

export const isCreatorPayoutEnrolled = (eligibility: LensCreatorPayoutEligibility): boolean => {
    return eligibility !== LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset;
};

export const isCreatorPayoutDisabled = (
    actionsDisabled: boolean,
    visibility: Visibility,
    hasExclusiveLensProgram: boolean,
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility,
): boolean => {
    return (
        actionsDisabled ||
        visibility === Visibility.VisibilityDontPublishToSnapchat ||
        hasExclusiveLensProgram ||
        lensCreatorPayoutEligibility !== LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset
    );
};

export const isLensCreationWithinThreshold = (creationDate: Date | null): boolean => {
    if (!creationDate) {
        return false;
    }
    return isBefore(new Date(), addDays(creationDate, CREATOR_REWARDS_DAYS_THRESHOLD));
};

interface MonetizationHistoryPathScope {
    isHubEnabled: boolean;
    isExclusiveLens: boolean;
    orgId?: string | null;
    lensFolderId?: string | null;
}

export const getMonetizationHistoryPath = ({
    isHubEnabled,
    isExclusiveLens,
    orgId,
    lensFolderId,
}: MonetizationHistoryPathScope): string => {
    const tab = isExclusiveLens ? MonetizationTab.LensPlus : MonetizationTab.TopPerformer;
    return isHubEnabled
        ? buildMonetizationTabPath({ orgId, lensFolderId, tab })
        : buildInsightsTabPath({ orgId, lensFolderId, insightsTab: RouteEntities.Monetization });
};

interface ProgramEnrollment {
    isExclusiveLens: boolean;
    topPerformerEnrolled: boolean;
    hasLensPlusHistory: boolean;
}

// Dual enrollment is real data (exclusivity is client-side only); callers fall back to the legacy body.
export const resolveEnrolledProgram = ({
    isExclusiveLens,
    topPerformerEnrolled,
    hasLensPlusHistory,
}: ProgramEnrollment): MonetizationTab | null => {
    const isInBothOrNeither = isExclusiveLens === topPerformerEnrolled;
    if (isInBothOrNeither) {
        return null;
    }
    if (isExclusiveLens) {
        return MonetizationTab.LensPlus;
    }
    // payoutStats isn't program-scoped (LOOK-171953), so a former Lens+ lens would list Lens+ months as awards.
    return hasLensPlusHistory ? null : MonetizationTab.TopPerformer;
};
